<script setup>
// Gradient page-header band for public catalog + detail pages. Since the
// 2026-09 restyle every kind sits on the same sea-blue family (tour/package/
// delivery lean lagoon); only `vip` (ink + gold) and `painting` differ. `top` slot = back link; default slot = lede / price
// / CTAs under the title.
defineProps({
  kind:    { type: String, default: 'vehicle' }, // vehicle | tour | property | legal | delivery | invest | transfer | package | vip | charter
  eyebrow: { type: String, default: '' },
  title:   { type: String, required: true },
})
</script>

<template>
  <section :class="['bhero', `bhero--${kind}`]">
    <svg class="bhero__sun" viewBox="0 0 120 120" width="416" height="416" aria-hidden="true">
      <g stroke="currentColor" stroke-width="6" stroke-linecap="round">
        <line x1="60" y1="6" x2="60" y2="30" /><line x1="60" y1="90" x2="60" y2="114" />
        <line x1="6" y1="60" x2="30" y2="60" /><line x1="90" y1="60" x2="114" y2="60" />
        <line x1="22" y1="22" x2="38" y2="38" /><line x1="82" y1="82" x2="98" y2="98" />
        <line x1="98" y1="22" x2="82" y2="38" /><line x1="38" y1="82" x2="22" y2="98" />
      </g>
      <circle cx="60" cy="60" r="22" fill="currentColor" />
    </svg>
    <div class="bhero__inner">
      <slot name="top" />
      <p v-if="eyebrow" class="bhero__eyebrow">{{ eyebrow }}</p>
      <h1 class="bhero__title">{{ title }}</h1>
      <slot />
    </div>
  </section>
</template>

<style scoped>
.bhero { position: relative; overflow: hidden; width: 100%; }
.bhero__sun {
  position: absolute; top: -7rem; right: -3rem; width: 26rem; height: 26rem;
  color: currentColor; opacity: 0.14; pointer-events: none;
}
.bhero__inner {
  max-width: 96rem; margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 5rem) clamp(1.25rem, 4vw, 3rem) clamp(2.25rem, 5vw, 4rem);
  position: relative;
}
.bhero__eyebrow {
  font-size: 0.8rem; letter-spacing: 0.2em; text-transform: uppercase;
  font-weight: 700; margin: 0 0 0.7rem; opacity: 0.88;
}
.bhero__title {
  font-family: var(--font-display, var(--display)); font-weight: 600;
  font-size: clamp(2.35rem, 5.5vw, 3.6rem); line-height: 1.02;
  letter-spacing: -0.015em; margin: 0; max-width: 20ch; text-wrap: balance;
}

/* Colourways (2026-09-06 restyle) — ONE sea family. Every catalog and
   detail band sits on the azure → deep-sea gradient the home and charters
   use; experiences/packages/delivery lean a touch more lagoon. Sunset
   (--accent) is reserved for buttons and small accents, never a hero
   ground. Ink + gold stays for VIP only (a deliberately darker tier);
   painting keeps its own street-art band. */
.bhero {
  color: var(--accentInk);
  background: radial-gradient(120% 130% at 85% 8%, color-mix(in oklch, var(--azure, var(--second)) 72%, white) 0%, var(--azure, var(--second)) 46%, color-mix(in oklch, var(--night, var(--ink)) 70%, var(--azure, var(--second))) 100%);
}
.bhero--tour,
.bhero--package,
.bhero--delivery {
  background: radial-gradient(120% 130% at 85% 8%, color-mix(in oklch, var(--second) 74%, white) 0%, color-mix(in oklch, var(--second) 62%, var(--azure, var(--second))) 48%, color-mix(in oklch, var(--night, var(--ink)) 62%, var(--azure, var(--second))) 100%);
}
/* VIP/Luxury — deep ink base with a gold (--warn) glow, matching the gold
   ProductCard VIP badge so the aggregation page reads as a distinct,
   premium tier rather than another catalog colourway. */
.bhero--vip {
  color: var(--bg);
  background: radial-gradient(120% 130% at 85% 8%, color-mix(in oklch, var(--warn) 45%, var(--ink)) 0%, var(--ink) 55%, color-mix(in oklch, var(--ink) 88%, black) 100%);
}
/* Painting / mural studio — a bold multi-hue spray-paint band (accent coral →
   second → gold) over ink, evoking street art rather than a corporate header. */
.bhero--painting {
  color: var(--bg);
  background:
    radial-gradient(90% 120% at 12% 12%, color-mix(in oklch, var(--accent) 70%, transparent) 0%, transparent 55%),
    radial-gradient(90% 120% at 88% 20%, color-mix(in oklch, var(--warn) 65%, transparent) 0%, transparent 55%),
    linear-gradient(150deg, color-mix(in oklch, var(--ink) 85%, var(--second)) 0%, var(--ink) 70%);
}
.bhero--vip .bhero__eyebrow { color: var(--warn); opacity: 1; }
</style>
